import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, useWixSdk, useBi } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';
import useObservable from '../../Shared/Hooks/useObservable.Hook';
import {
  getAppSettings,
  setAppSettings,
} from '../Services/AppSettings.service';
import {
  setRequestLogin,
  setUser,
  setNavigateToPricingPlans,
} from '../Services/User.service';
import settingsParams from '../../settingsParams';
import {
  setSelectedFolder,
  setSelectedFolderId,
} from '../Services/SelectedFolder.service';
import { setRootFolder } from '../Services/RootFolder.service';
import { searchObservable$ } from '../Observables/Search.observable';
import { setBiEventsParams } from '../Services/BiEvents.service';
import biService from '../BiEvents/BiService';
import { BI_FILE_SHARE_WIDGET_APP_LOADED } from '../BiEvents/Constants/BiConstants';
import { getBreadCrumbs } from '../Services/BreadCrumbs.service';
import {
  GetAllFolders,
  setAllFolders,
} from '../../Settings/Services/Folders.service';
import { setToast } from '../Services/Toast.service';
import { TOAST } from '../../Constants/Toast.Constants';
import { setSelectedFolderIdInSettings } from '../Shared/add-ons/helpers';
import { setIsLoading } from '../Services/Loading.service';
import { APP_DEF_ID_FILE_SHARE_PROD } from '../../Constants/AppSettings.Constant';
import type { ControllerProps } from '../types';

export function useWidgetSetup(props: ControllerProps) {
  const {
    isMobile,
    user,
    baseUrl,
    instance: rawInstance,
    requestLogin,
    navigateToPricingPlansApp,
    appParams,
    isEditor_X,
    biToken,
    isEditor,
    isClonedSite,
    membersError,
  } = props;
  const compId = props.compId ?? props.id;
  const appDefinitionId = appParams?.appDefinitionId;
  const instanceId = appParams?.instanceId;
  const userId = user?.id;
  const userRole = user?.role;

  const { t }: { t: any } = useTranslation();
  const settings = useSettings();

  // Decode the tenant (appDefId) from an instance token.
  const getInstanceAppDefId = (inst?: string) => {
    try {
      return JSON.parse(atob((inst || '').split('.')[1] || ''))?.appDefId;
    } catch (e) {
      return undefined;
    }
  };

  // On a cloned site in the editor, getAppToken can't mint the File Share PROD token, so
  // editor.app.ts writes it into publicData; the widget reads it here (reactive). Prefer it ONLY
  // when the controller-provided instance is not already PROD — i.e. the editor-clone case.
  // Preview/live already receive PROD from the controller, and regular sites never set this.
  const prodInstanceFromSettings = settings.get(settingsParams.prodInstance);
  const preferredRawInstance =
    getInstanceAppDefId(prodInstanceFromSettings) ===
      APP_DEF_ID_FILE_SHARE_PROD &&
    getInstanceAppDefId(rawInstance) !== APP_DEF_ID_FILE_SHARE_PROD
      ? prodInstanceFromSettings
      : rawInstance;

  // Use the instance directly (original behavior — picks up the controller's post-login token
  // refresh). On regular sites this is just rawInstance; on clones it's the PROD token from
  // publicData once available. The render/fetch loop is prevented by the effect split below
  // (folder loading no longer depends on memoizedValue/dimensions), not by locking the instance.
  const instance = preferredRawInstance;
  const [, appSettings]: any = useObservable(getAppSettings());
  const [, breadCrumbs]: any = useObservable(getBreadCrumbs(compId));
  const [state, setState] = useState({ isLoadingBiEventSent: false });
  const host = props.host ?? {};
  const { accessibilityEnabled, dimensions, formFactor, viewMode, scrollTo } =
    host;

  const { Wix } = useWixSdk();
  useObservable(searchObservable$(compId));
  const bi = useBi();
  const requestLoginRef = useRef(requestLogin);
  const navigateToPricingPlansAppRef = useRef(navigateToPricingPlansApp);
  const biRef = useRef(bi);
  requestLoginRef.current = requestLogin;
  navigateToPricingPlansAppRef.current = navigateToPricingPlansApp;
  biRef.current = bi;
  const selectedFolderIdFromSettings = settings.get(settingsParams.folderId);
  const folderNameFromSettings = settings.get(settingsParams.folderName);
  const rtlStatus = settings.get(settingsParams.alignment);

  const memoizedValue = useMemo(() => {
    return {
      accessibilityEnabled,
      dimensions,
      formFactor,
      viewMode,
      isMobile,
      instance,
      t,
      baseUrl,
      isEditor_X,
      isEditor,
      scrollTo,
    };
  }, [
    isEditor_X,
    accessibilityEnabled,
    dimensions,
    formFactor,
    instance,
    isMobile,
    t,
    viewMode,
    baseUrl,
    isEditor,
    scrollTo,
  ]);

  const _getAllFolders = useCallback(async () => {
    const result = await GetAllFolders('widget');
    if (result && result?.data && result?.data?.libraryItems?.length === 0) {
      setSelectedFolderIdInSettings('folderId', '', isEditor, Wix);
      setAllFolders(result.data);
      return;
    }
    if (result && result?.data && result?.data?.libraryItems?.length > 0) {
      const items = result.data.libraryItems;
      const idMatch =
        selectedFolderIdFromSettings &&
        selectedFolderIdFromSettings !== '-1'
          ? items.find((item: any) => item.id === selectedFolderIdFromSettings)
          : null;
      const nameMatch =
        !idMatch && folderNameFromSettings
          ? items.find((item: any) => item.name === folderNameFromSettings)
          : null;
      const target = idMatch ?? nameMatch ?? items[0];

      if (!idMatch) {
        // ID is absent, -1, or stale (cross-tenant clone). Correct it; the effect
        // will re-fire with the new ID and call setSelectedFolderId from the normal path.
        setSelectedFolderIdInSettings('folderId', target.id, isEditor, Wix);
        setSelectedFolderIdInSettings(
          'selectedFolderIdInLiveSite',
          target.id,
          isEditor,
          Wix,
        );
        setRootFolder(target.id, compId);
      } else {
        setRootFolder(target.id, compId);
        setSelectedFolderId(target.id, compId);
      }
      setAllFolders(result.data);
      return;
    }
    setToast({
      skin: TOAST.error,
      content: result?.message,
    });
  }, [selectedFolderIdFromSettings, folderNameFromSettings, Wix, compId, isEditor]);

  // Keep appSettings in sync with layout/i18n/instance. This is cheap — no fetch and no
  // loading toggle — so host `dimensions` re-measures (fitToContentHeight) can update
  // appSettings without ever driving a data fetch, a loading toggle, or a render/fetch loop.
  useEffect(() => {
    if (instance) {
      setAppSettings(memoizedValue);
    }
  }, [memoizedValue, instance]);

  // Bootstrap user / login / pricing + BI params. Independent of layout and folder selection.
  useEffect(() => {
    setBiEventsParams(
      {
        visitor_id: userId,
        app_id: appDefinitionId,
        instance_id: instanceId,
        logger: biRef.current,
        comp_id: compId,
        owner: userRole,
        biToken,
      },
      compId,
    );
    setUser(user);
    setRequestLogin(requestLoginRef.current);
    setNavigateToPricingPlans(navigateToPricingPlansAppRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, compId, userId, userRole, appDefinitionId, instanceId, biToken]);

  // Apply the selected folder whenever it changes. This is the ONLY place that drives folder
  // loading, and it depends solely on the selected id + instance — NOT on dimensions/
  // memoizedValue (which oscillate). So layout re-measures never re-trigger a fetch or a
  // loading toggle (the old infinite loop) and a real folder change always applies. The actual
  // content fetch is done by FolderTable reacting to the selectedFolderId observable set here.
  useEffect(() => {
    if (!instance) {
      return;
    }
    if (
      selectedFolderIdFromSettings === '-1' ||
      (!folderNameFromSettings &&
        selectedFolderIdFromSettings &&
        selectedFolderIdFromSettings !== '') ||
      (isClonedSite &&
        selectedFolderIdFromSettings &&
        selectedFolderIdFromSettings !== '')
    ) {
      // '-1' → first-run; no folderName → pre-fix or stale clone ID; isClonedSite →
      // always validate on clones since the stored ID (original-site PROD UUID) will
      // not match the clone's PROD tenant UUIDs even when folderName is present.
      _getAllFolders();
    } else if (
      selectedFolderIdFromSettings &&
      selectedFolderIdFromSettings !== ''
    ) {
      setIsLoading(true, compId);
      setRootFolder(selectedFolderIdFromSettings, compId);
      setSelectedFolderId(selectedFolderIdFromSettings, compId);
    } else {
      setRootFolder('', compId);
      setSelectedFolderId('', compId);
      setSelectedFolder(undefined, compId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderIdFromSettings, instance, compId]);

  useEffect(() => {
    if (!state?.isLoadingBiEventSent && breadCrumbs !== undefined) {
      biService(
        {
          folder_id: selectedFolderIdFromSettings,
          folder_name: breadCrumbs[0]?.value,
          isEditor: viewMode === 'Editor',
        },
        BI_FILE_SHARE_WIDGET_APP_LOADED,
        compId,
      );
      setState((prevState) => ({ ...prevState, isLoadingBiEventSent: true }));
    }
  }, [
    breadCrumbs,
    compId,
    selectedFolderIdFromSettings,
    state?.isLoadingBiEventSent,
    viewMode,
  ]);

  useEffect(() => {
    if (membersError) {
      setToast({
        skin: TOAST.error,
        content: membersError,
      });
    }
  }, [membersError]);

  return { selectedFolderIdFromSettings, breadCrumbs, rtlStatus };
}
